import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  makeStyles,
  tokens,
  Title1,
  Text,
  Badge,
  Spinner,
  Button,
} from '@fluentui/react-components';
import {
  listRecentTraces,
  getTrace,
  type TraceListItem,
  type TraceDetail,
} from '../api/traces';
import { TraceStepList } from '../components/TraceStepList';

// Workbench MVP Session A — Flight Recorder page.
// Left: recent traces list (limit=50). Right: detail panel with step timeline.
// Deep-link via ?trace=<traceId>, per session-16 useSearchParams precedent.

const useStyles = makeStyles({
  header: { marginBottom: '18px' },
  root: {
    display: 'grid',
    gridTemplateColumns: '360px 1fr',
    gap: '20px',
  },
  list: {
    background: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: '8px',
    padding: '10px 12px',
    maxHeight: '78vh',
    overflowY: 'auto' as const,
  },
  listHeader: {
    fontWeight: 600,
    marginBottom: '8px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  item: {
    padding: '10px 8px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke3}`,
    cursor: 'pointer',
    borderRadius: '4px',
  },
  itemActive: {
    background: tokens.colorNeutralBackground1Selected,
  },
  itemTitle: {
    fontSize: '13px',
    fontWeight: 500,
    marginBottom: '3px',
    overflow: 'hidden' as const,
    textOverflow: 'ellipsis' as const,
    whiteSpace: 'nowrap' as const,
  },
  itemMeta: {
    fontSize: '11px',
    color: tokens.colorNeutralForeground3,
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
    fontVariantNumeric: 'tabular-nums' as const,
  },
  detail: {
    minHeight: '300px',
  },
  detailCard: {
    background: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: '8px',
    padding: '14px 16px',
    marginBottom: '14px',
  },
  kv: {
    display: 'grid',
    gridTemplateColumns: 'auto 1fr',
    gap: '4px 14px',
    marginTop: '8px',
    fontSize: '13px',
    fontVariantNumeric: 'tabular-nums' as const,
  },
  key: { color: tokens.colorNeutralForeground3 },
  empty: {
    color: tokens.colorNeutralForeground3,
    fontStyle: 'italic',
    padding: '30px 0',
    textAlign: 'center' as const,
  },
  err: { color: tokens.colorPaletteRedForeground1 },
});

function fmtMs(v: number | null): string {
  if (v === null || v === undefined) return '—';
  if (v >= 1000) return `${(v / 1000).toFixed(2)}s`;
  return `${v.toFixed(0)}ms`;
}

function fmtTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

export default function FlightRecorder() {
  const s = useStyles();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedId = searchParams.get('trace');

  const [items, setItems] = useState<TraceListItem[] | null>(null);
  const [detail, setDetail] = useState<TraceDetail | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loadingList, setLoadingList] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);

  // Load the list once.
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const list = await listRecentTraces(50);
        if (mounted) setItems(list);
      } catch (e) {
        if (mounted) setErr((e as Error).message);
      } finally {
        if (mounted) setLoadingList(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  // Load detail whenever the URL selection changes.
  useEffect(() => {
    if (!selectedId) {
      setDetail(null);
      return;
    }
    let mounted = true;
    setLoadingDetail(true);
    (async () => {
      try {
        const d = await getTrace(selectedId);
        if (mounted) setDetail(d);
      } catch (e) {
        if (mounted) setErr((e as Error).message);
      } finally {
        if (mounted) setLoadingDetail(false);
      }
    })();
    return () => { mounted = false; };
  }, [selectedId]);

  function select(traceId: string) {
    // Preserve other params if any get added later.
    const next = new URLSearchParams(searchParams);
    next.set('trace', traceId);
    setSearchParams(next);
  }

  return (
    <div>
      <div className={s.header}>
        <Title1>Flight Recorder</Title1>
        <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
          Recent benchmark and chat traces with step-level timing.
        </Text>
      </div>

      {err && <div className={s.err}>Error: {err}</div>}

      <div className={s.root}>
        <div className={s.list}>
          <div className={s.listHeader}>
            <span>Recent traces</span>
            <Button size="small" appearance="subtle" onClick={() => {
              setLoadingList(true);
              listRecentTraces(50)
                .then(setItems)
                .catch((e) => setErr((e as Error).message))
                .finally(() => setLoadingList(false));
            }}>Refresh</Button>
          </div>
          {loadingList ? (
            <Spinner size="tiny" label="Loading…" />
          ) : items && items.length > 0 ? (
            items.map((item) => (
              <div
                key={item.traceId}
                className={`${s.item} ${item.traceId === selectedId ? s.itemActive : ''}`}
                onClick={() => select(item.traceId)}
              >
                <div className={s.itemTitle}>{item.label || '(no label)'}</div>
                <div className={s.itemMeta}>
                  <Badge
                    appearance="tint"
                    color={item.runType === 'benchmark' ? 'brand' : 'informative'}
                  >
                    {item.runType}
                  </Badge>
                  <span>{fmtTime(item.startedAt)}</span>
                  <span>{fmtMs(item.totalDurationMs)}</span>
                  {item.hasErrors && <Badge appearance="tint" color="danger">err</Badge>}
                  {item.hasSlaBreach && <Badge appearance="tint" color="warning">SLA</Badge>}
                </div>
              </div>
            ))
          ) : (
            <div className={s.empty}>No traces yet. Run a benchmark or a chat query.</div>
          )}
        </div>

        <div className={s.detail}>
          {!selectedId ? (
            <div className={s.detailCard}>
              <div className={s.empty}>Select a trace from the list to inspect its step timeline.</div>
            </div>
          ) : loadingDetail ? (
            <Spinner label="Loading trace…" />
          ) : detail ? (
            <>
              <div className={s.detailCard}>
                <Text weight="semibold">{detail.label || '(no label)'}</Text>
                <div className={s.kv}>
                  <span className={s.key}>traceId</span>
                  <span>{detail.traceId}</span>
                  <span className={s.key}>runType</span>
                  <span>{detail.runType}</span>
                  <span className={s.key}>startedAt</span>
                  <span>{fmtTime(detail.startedAt)}</span>
                  <span className={s.key}>total</span>
                  <span>{fmtMs(detail.totalDurationMs)}</span>
                  <span className={s.key}>steps</span>
                  <span>{detail.stepCount}</span>
                  <span className={s.key}>providers</span>
                  <span>{detail.providerCount}</span>
                  <span className={s.key}>errors</span>
                  <span>{detail.hasErrors ? 'yes' : 'no'}</span>
                  <span className={s.key}>SLA</span>
                  <span>{detail.hasSlaBreach ? 'breach' : 'ok'}</span>
                </div>
              </div>
              <TraceStepList steps={detail.steps} />
            </>
          ) : (
            <div className={s.detailCard}>
              <div className={s.err}>Trace not found.</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
